/* Kimi (Moonshot AI) vault — API-key + taak-toggles. */
import GenericVault from "./GenericVault";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BrainCircuit } from "lucide-react";

/* ----------------------------------------------------------------- */
/*  KimiVault — Moonshot API-key vault + taak-toggles: welke AI-taken */
/*  via Kimi K2.6 lopen (drafts / bulk / CV-parsing). Claude blijft   */
/*  altijd het automatische fallback-pad wanneer Kimi faalt.          */
/* ----------------------------------------------------------------- */
const KIMI_TASKS = [
    { key: "use_for_drafts", labelKey: "integr.kimi_task_drafts", subKey: "integr.kimi_task_drafts_sub" },
    { key: "use_for_bulk",   labelKey: "integr.kimi_task_bulk",   subKey: "integr.kimi_task_bulk_sub" },
    { key: "use_for_cv",     labelKey: "integr.kimi_task_cv",     subKey: "integr.kimi_task_cv_sub" },
];

function KimiVault({ provider, cfg, onSaved }) {
    const { t } = useTranslation();
    const settings = cfg.settings || {};
    const [flags, setFlags] = useState({
        use_for_drafts: !!settings.use_for_drafts,
        use_for_bulk: !!settings.use_for_bulk,
        use_for_cv: !!settings.use_for_cv,
    });
    const [saving, setSaving] = useState(null); // key die opslaat

    useEffect(() => {
        const s = cfg.settings || {};
        setFlags({ use_for_drafts: !!s.use_for_drafts, use_for_bulk: !!s.use_for_bulk, use_for_cv: !!s.use_for_cv });
    }, [cfg]);

    const toggle = async (key, next) => {
        setFlags((f) => ({ ...f, [key]: next })); setSaving(key);
        try {
            await api.patch("/integrations/kimi", { settings: { [key]: next } });
            toast.success(t("integr.kimi_task_saved"));
            onSaved?.();
        } catch (e) {
            setFlags((f) => ({ ...f, [key]: !next }));
            toast.error(e?.response?.data?.detail || t("integr.save_failed"));
        } finally { setSaving(null); }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-[#14e0c8]/20 bg-[#14e0c8]/[0.04] p-4" data-testid="kimi-hint">
                <div className="flex items-start gap-2.5">
                    <BrainCircuit size={16} className="text-[#14e0c8] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#14e0c8]">{t("integr.kimi_hint_title")}</div>
                        <p className="text-[11px] text-[var(--jr-text-2)] mt-1 leading-relaxed">{t("integr.kimi_hint")}</p>
                    </div>
                </div>
            </div>

            <GenericVault provider={provider} cfg={cfg} onSaved={onSaved} appBaseUrl="" />

            <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)]/40 p-4 space-y-3" data-testid="kimi-tasks-panel">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#14e0c8]">{t("integr.kimi_tasks_title")}</div>
                {KIMI_TASKS.map(({ key, labelKey, subKey }) => (
                    <label key={key}
                        className={`flex items-start gap-2.5 select-none ${cfg.configured ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
                        data-testid={`kimi-toggle-${key}`}>
                        <input type="checkbox" checked={flags[key]} disabled={!!saving || !cfg.configured}
                            onChange={(e) => toggle(key, e.target.checked)}
                            className="accent-[#14e0c8] mt-0.5" />
                        <span className="min-w-0">
                            <span className="text-xs text-white inline-flex items-center gap-1.5">
                                {t(labelKey)}
                                {saving === key && <Loader2 size={10} className="animate-spin text-[var(--jr-text-3)]" />}
                            </span>
                            <span className="block text-[11px] text-[var(--jr-text-3)] mt-0.5 leading-relaxed">{t(subKey)}</span>
                        </span>
                    </label>
                ))}
                {!cfg.configured && (
                    <p className="text-[11px] text-[var(--jr-text-3)] italic">{t("integr.kimi_need_key")}</p>
                )}
            </div>
        </div>
    );
}

export default KimiVault;
